import type { Mail } from "@agentsaas/protocol";

export interface MailThread {
  id: string;
  subject: string;
  mails: Mail[];
  last: Mail;
  /** 该 agent 收到但还没读的封数 */
  unread: number;
  /** 这个线程里该 agent 有没有收到 / 发出 */
  hasInbox: boolean;
  hasSent: boolean;
}

/**
 * 把一个 agent 的收件箱 + 发件箱按线程归并：同一封信可能同时出现在两边（发给自己），按 id 去重；
 * 线程内按时间正序，线程之间按最后一封倒序。主题取第一封（去掉回复前缀）。
 */
export function buildThreads(aid: string, inbox: Mail[], sent: Mail[]): MailThread[] {
  const byId = new Map<string, Mail>();
  for (const m of [...inbox, ...sent]) byId.set(m.id, m);
  const groups = new Map<string, Mail[]>();
  for (const m of byId.values()) {
    const g = groups.get(m.threadId) ?? [];
    g.push(m);
    groups.set(m.threadId, g);
  }
  const inboxIds = new Set(inbox.map((m) => m.id));
  const sentIds = new Set(sent.map((m) => m.id));
  return [...groups.entries()]
    .map(([id, mails]) => {
      mails.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return {
        id,
        subject: stripReply(mails[0]!.subject),
        mails,
        last: mails[mails.length - 1]!,
        unread: mails.filter((m) => inboxIds.has(m.id) && !m.readBy.includes(aid as Mail["to"][number])).length,
        hasInbox: mails.some((m) => inboxIds.has(m.id)),
        hasSent: mails.some((m) => sentIds.has(m.id)),
      };
    })
    .sort((a, b) => b.last.createdAt.localeCompare(a.last.createdAt));
}

export function stripReply(s: string): string {
  return s.replace(/^\s*((re|fw|fwd|回复|转发)\s*[:：]\s*)+/i, "").trim() || s;
}
